import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { cloneDeep } from "lodash";
import set from "lodash/set";
import get from "lodash/get";
// components
import { sortTriangle } from "@assets/images";
import EditableLabel from "./EditableLabel";
import IconClose from "@assets/icons/IconClose";
// actions
import {
  recordChangeObject,
  logChangedKey,
  setToastMessage,
} from "@actions/global";
import { loadConfig, setDeletedFields } from "@features/schema-management/actions";
// utils
import { logDiffs } from "../utils/schemaManagementHelpers";
import { isEmptyObject } from "@utils/object-helpers";
// constants
import { ATHOS_DEFAULT_PATTERN_PROPERTY_KEY } from "@constants/schema-management";

const CustomTitleFieldTemplate = (props) => {
  const {
    id,
    label,
    value,
    placeholder,
    required,
    disabled,
    onChange,
    name,
    title,
    schema,
    ...rest
  } = props;
  const dispatch = useDispatch();
  const { type } = schema || "";
  const isAthosConfig = useSelector(
    (state) => state.schemaAllView.isAthosConfig
  );
  const schemaManagementConfig = useSelector(
    (state) => state.schemaAllView.schemaManagementConfig
  );
  const defaultConfig = useSelector(
    (state) => state.schemaAllView.defaultConfig
  );
  const initialConfig = useSelector(
    (state) => state.schemaAllView.initialConfig
  );
  const featureName = useSelector((state) => state.schemaAllView.featureName);
  const deletedFields = useSelector(
    (state) => state.schemaAllView.deletedFields
  );
  const deletedClients = useSelector(
    (state) => state.schemaAllView.deletedClients
  );
  const [visible, setVisibility] = useState(true);
  const [removed, setRemoved] = useState(false);
  const [localDeletedFields, setLocalDeletedFields] = useState([]);
  const removable =
    isAthosConfig &&
    (type === "object" || type.includes("object")) &&
    !required;
  const rootValue = id.toLowerCase().split(title.toLowerCase())[0] === "root.";
  const path = id.match(/root\.(.*?)__title/)[1];
  const localClient = path.split(".")[0];

  useEffect(() => {
    if (!isEmptyObject(deletedFields) && deletedFields[localClient]) {
      setLocalDeletedFields(deletedFields[localClient]);
      setRemoved(deletedFields[localClient].includes(path));
      setVisibility(!deletedFields[localClient].includes(path));
    }
  }, [deletedFields]);

  const handleFieldAction = (action) => {
    const deleteAction = action === "delete";
    setRemoved(!removed);
    setVisibility(!visible);
    const config = cloneDeep(schemaManagementConfig);
    let newValue = null;
    if (!deleteAction) {
      const keyPath = path.split(localClient)[1];
      const defaultKey = isAthosConfig
        ? ATHOS_DEFAULT_PATTERN_PROPERTY_KEY
        : "";
      const defaultPath = `${defaultKey}${keyPath}`;
      newValue = get(defaultConfig, defaultPath);
    }
    set(config, path, newValue);
    dispatch(loadConfig(config));
    let loggedDiffs = {
      ...deletedClients,
      ...logDiffs(initialConfig, config, featureName),
    };
    dispatch(recordChangeObject(loggedDiffs));
    dispatch(logChangedKey(Object.keys(loggedDiffs)));
    const updatedDeletedFields = deleteAction
      ? localDeletedFields.concat(path)
      : localDeletedFields.filter((el) => el !== path);
    dispatch(setDeletedFields(updatedDeletedFields, localClient));
    dispatch(
      setToastMessage(
        deleteAction
          ? `Field '${path}' has been deleted.`
          : `Deleted field '${path}' has been reset.`
      )
    );
  };

  return rootValue ? (
    <EditableLabel value={title} {...props} />
  ) : (
    <legend className={`flex w-full ${!visible ? "hide-sibling" : ""}`} id={id}>
      {title}{" "}
      {removed ? (
        <div
          className="font-normal ml-4 text-color-teal cursor-pointer hover:opacity-50"
          onClick={() => handleFieldAction()}
        >
          Reset
        </div>
      ) : (
        <img
          src={!visible ? sortTriangle.up : sortTriangle.down}
          className="h-max my-auto mx-2 cursor-pointer"
          onClick={() => setVisibility(!visible)}
        />
      )}
      {removable && !removed && (
        <IconClose
          iconFillColor="fill-black"
          className="cursor-pointer w-3.5 m-auto mr-0"
          onClick={() => handleFieldAction("delete")}
        />
      )}
    </legend>
  );
};

export default CustomTitleFieldTemplate;
